import React, { useState, useEffect } from "react";
import { Card, Row, Col, Label } from "reactstrap";
import Dropzone from "react-dropzone";
import { fetchExistingFile, uploadFile, deleteFile, toBase64 } from '../../helpers/Espo/attachment_helper';

const SingleFileUpload = ({ title, name, fileId, fileName, relatedId, onFileUpload, disabled = false, required = false }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [existingFile, setExistingFile] = useState(null);
    const [existingFileUrl, setExistingFileUrl] = useState(null);
    const [uploadedFile, setUploadedFile] = useState(null); // Track the uploaded file
    const [uploadedFileId, setUploadedFileId] = useState(null); // Track the uploaded file's ID
    const [fileTypeError, setFileTypeError] = useState("")

    useEffect(() => {
        // Reset state if no fileId is provided
        if (!fileId) {
            setSelectedFile(null);
            setExistingFile(null);
            setExistingFileUrl(null);
            setUploadedFile(null);
            setUploadedFileId(null);
        }

        if (relatedId && fileId) {
            const loadExistingFile = async () => {
                try {
                    const response = await fetchExistingFile(fileId);
                    if (response) {
                        setExistingFile(response);
                        const fileURL = URL.createObjectURL(response);
                        setExistingFileUrl(fileURL);
                    }
                } catch (error) {
                    console.error("Error loading file:", error);
                }
            };
            loadExistingFile();
        }
    }, [relatedId, fileId]);

    const handleAcceptedFiles = (files) => {
        // Prevent file selection if component is disabled or a file already exists/uploaded
        if (disabled || existingFile || uploadedFile) {
            return;
        }
        
        setFileTypeError("");
        const file = files[0];
        if (!file) return;

        // Check file size (5MB = 5 * 1024 * 1024 bytes)
        const maxFileSize = 5 * 1024 * 1024; // 5MB in bytes
        if (file.size > maxFileSize) {
            setFileTypeError("File size must be less than 5MB. Please select a smaller file.");
            setSelectedFile(null);
            return;
        }

        // Define allowed file types based on field name
        const imageOnlyFields = ['aadharfront', 'aadharback', 'passportphoto'];
        const isImageOnlyField = imageOnlyFields.some(field => 
            name.toLowerCase().includes(field.toLowerCase())
        );

        let allowedTypes;
        let errorMessage;

        if (isImageOnlyField) {
            // For Aadhar and passport photo - only images allowed
            allowedTypes = [
                "image/jpeg",
                "image/png",
                "image/jpg"
            ];
            errorMessage = "Only image files (JPEG, PNG, JPG) are allowed for this field.";
        } else {
            // For other fields - images and PDFs allowed
            allowedTypes = [
                "image/jpeg",
                "image/png",
                "image/jpg",
                "application/pdf",
            ];
            errorMessage = "Only image or PDF files are allowed.";
        }

        if (!allowedTypes.includes(file.type)) {
            setFileTypeError(errorMessage);
            setSelectedFile(null);
            return;
        }
        setSelectedFile(file);
        // onFileUpload(file); // Notify parent component about the file upload
    };

    const handleUpload = async () => {
        // Prevent upload if component is disabled or a file already exists/uploaded
        if (disabled || existingFile || uploadedFile) {
            return;
        }
        
        let relatedtype = "MMApplicationNew";
        if (!selectedFile) {
            alert("No file selected for upload.");
            return;
        } else {
            console.log("name:", name);
            if (name.toLowerCase().includes("aadhar")) {
                relatedtype = "MMApplicant";
            }
        }

        try {
            const base64File = await toBase64(selectedFile);
            const payload = {
                name: selectedFile.name,
                type: selectedFile.type,
                role: "Attachment",
                relatedType: relatedtype,
                relatedId: relatedId,
                field: name,
                file: base64File,
            };

            console.log("Payload:", payload);

            const response = await uploadFile(payload);
            console.log("File uploaded successfully:", response);

            // Treat uploaded file as the "existing file"
            setUploadedFile(selectedFile);
            setUploadedFileId(response.id); // Store the uploaded file ID
            setExistingFile(null);
            setSelectedFile(null);

            // Notify parent component about the file upload
            if (onFileUpload) {
                onFileUpload(response);
            }

            alert("File uploaded successfully");
        } catch (error) {
            console.error("Error during upload:", error);
            alert(`Upload failed: ${error.message}`);
        }
    };

    const handleRemoveFile = async () => {
        // Prevent file removal if component is disabled
        if (disabled) {
            return;
        }
        
        // Show confirmation dialog
        const confirmDelete = window.confirm("Are you sure you want to remove this file? This action cannot be undone.");
        if (!confirmDelete) {
            return;
        }
        
        try {
            // Determine which file to delete based on what's currently set
            let fileIdToDelete = null;
            let shouldDeleteFromBackend = false;
            
            // Priority order: existing file ID from props (saved file), then uploaded file ID
            if (fileId) {
                // This is an existing file that was previously saved
                fileIdToDelete = fileId;
                shouldDeleteFromBackend = true;
            } else if (uploadedFileId) {
                // This is a newly uploaded file in current session
                fileIdToDelete = uploadedFileId;
                shouldDeleteFromBackend = true;
            }
            
            // If we have a file to delete from the backend, delete it
            if (shouldDeleteFromBackend && fileIdToDelete && relatedId) {
                const relatedType = name.toLowerCase().includes("aadhar") ? "MMApplicant" : "MMApplicationNew";
                await deleteFile(fileIdToDelete, relatedId, relatedType, name);
                console.log("File deleted successfully from backend");
            }
            
            // Clear local state
            setSelectedFile(null);
            setExistingFile(null);
            setUploadedFile(null);
            setUploadedFileId(null);
            setExistingFileUrl(null);
            setFileTypeError("");
            
            // Notify parent component about file removal
            if (onFileUpload) {
                onFileUpload(null);
            }
            
            alert("File removed successfully");
            
        } catch (error) {
            console.error("Error removing file:", error);
            alert(`Failed to remove file: ${error.message}`);
        }
    };

    const formatBytes = (bytes, decimals = 2) => {
        if (bytes === 0) return "0 Bytes";
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ["Bytes", "KB", "MB", "GB", "TB"];

        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
    };

    // Check if this is a passbook upload field
    const isPassbookField = name.toLowerCase().includes("passbook");
    
    // Define file type restrictions based on field name
    const imageOnlyFields = ['aadharfront', 'aadharback', 'passportphoto'];
    const isImageOnlyField = imageOnlyFields.some(field => 
        name.toLowerCase().includes(field.toLowerCase())
    );
    
    // Set accept attribute based on field type
    const acceptTypes = isImageOnlyField ? ".jpg,.jpeg,.png" : ".jpg,.jpeg,.png,.pdf";
    
    // Check if a file already exists or has been uploaded
    const hasFile = existingFile || uploadedFile;
    const isUploadDisabled = disabled || hasFile;

    return (
        <React.Fragment>
            <Label htmlFor="fileUpload"></Label>
            <div className={`dropzone ${required && !hasFile ? 'border-danger' : ''}`}>
                <Dropzone onDrop={isUploadDisabled ? () => {} : handleAcceptedFiles} multiple={false} disabled={isUploadDisabled}>
                    {({ getRootProps, getInputProps }) => (
                        <div style={{ textAlign: "center" }}>
                            <div className={`dz-message needsclick ${isUploadDisabled ? 'disabled' : ''}`} 
                                 {...(isUploadDisabled ? {} : getRootProps())}
                                 style={isUploadDisabled ? { backgroundColor: '#f5f5f5', color: '#999', cursor: 'not-allowed' } : {}}>
                                <input {...getInputProps()} accept={acceptTypes} disabled={isUploadDisabled} />
                                <div className="mb-3">
                                    <i className="display-4 text-muted mdi mdi-cloud-upload-outline"></i>
                                </div>
                                <h4>
                                    {disabled ? 'File upload disabled in view mode' : 
                                     hasFile ? 'Remove current file to upload a new one' : 
                                     'Drop your file here'}
                                </h4>
                                {required && !hasFile && (
                                    <p className="text-danger mt-2" style={{ fontSize: "14px" }}>
                                        <i className="mdi mdi-asterisk me-1"></i>
                                        This file upload is required
                                    </p>
                                )}
                                {fileTypeError && <span className="text-danger" style={{ fontSize: "14px" }}>{fileTypeError}</span>}
                                {!hasFile && !disabled && (
                                    <p className="text-muted mt-2" style={{ fontSize: "12px" }}>
                                        <i className="mdi mdi-information-outline me-1"></i>
                                        {isImageOnlyField 
                                            ? 'Only image files (JPEG, PNG, JPG) are allowed. Max size: 5MB'
                                            : 'Allowed formats: Images (JPEG, PNG, JPG) or PDF. Max size: 5MB'
                                        }
                                    </p>
                                )}
                                {hasFile && !disabled && (
                                    <p className="text-info mt-2" style={{ fontSize: "14px" }}>
                                        Only one file can be uploaded. Please remove the current file to upload a new one.
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </Dropzone>
                <div className="dropzone-previews mt-3" id="file-previews">
                    {selectedFile && (
                        <Card className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete">
                            <div className="p-2">
                                <Row className="align-items-center">
                                    <Col className="col-auto">
                                        <img
                                            data-dz-thumbnail=""
                                            height="80"
                                            className="avatar-sm rounded bg-light"
                                            alt={selectedFile.name}
                                            src={URL.createObjectURL(selectedFile)}
                                        />
                                    </Col>
                                    <Col>
                                        <span className="text-muted font-weight-bold" style={{ cursor: 'default' }}>
                                            {selectedFile.name}
                                        </span>
                                        <p className="mb-0">
                                            <strong>{formatBytes(selectedFile.size)}</strong>
                                        </p>
                                    </Col>
                                </Row>
                            </div>
                        </Card>
                    )}
                    {existingFile && (
                        <Card className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete">
                            <div className="p-2">
                                <Row className="align-items-center">
                                    <Col className="col-auto">
                                        <img width="400" className="avatar-sm rounded bg-light" alt={fileName} src={existingFileUrl} />
                                    </Col>
                                    <Col>
                                        <span className="text-muted font-weight-bold" style={{ cursor: 'default' }}>
                                            {fileName}
                                        </span>
                                        <p className="mb-0">
                                            <strong>{formatBytes(existingFile.size)}</strong>
                                        </p>
                                    </Col>
                                    {!disabled && (
                                        <Col className="col-auto">
                                            <button 
                                                onClick={handleRemoveFile} 
                                                className="btn btn-danger"
                                            >
                                                Remove
                                            </button>
                                        </Col>
                                    )}
                                </Row>
                            </div>
                        </Card>
                    )}
                    {uploadedFile && (
                        <Card className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete">
                            <div className="p-2">
                                <Row className="align-items-center">
                                    <Col className="col-auto">
                                        <img
                                            data-dz-thumbnail=""
                                            height="80"
                                            className="avatar-sm rounded bg-light"
                                            alt={uploadedFile.name}
                                            src={URL.createObjectURL(uploadedFile)}
                                        />
                                    </Col>
                                    <Col>
                                        <span className="text-muted font-weight-bold" style={{ cursor: 'default' }}>
                                            {uploadedFile.name}
                                        </span>
                                        <p className="mb-0">
                                            <strong>{formatBytes(uploadedFile.size)}</strong>
                                        </p>
                                    </Col>
                                    {!disabled && (
                                        <Col className="col-auto">
                                            <button 
                                                onClick={handleRemoveFile} 
                                                className="btn btn-danger"
                                            >
                                                Remove
                                            </button>
                                        </Col>
                                    )}
                                </Row>
                            </div>
                        </Card>
                    )}
                </div>
            </div>
            {isPassbookField && (
                <div className="alert alert-info mb-2 mt-2" style={{ fontSize: '14px', padding: '8px 12px' }}>
                    <i className="mdi mdi-information me-2"></i>
                    <strong>Note:</strong> Kindly upload a copy of your bank passbook showing transactions from the last 1 year.
                </div>
            )}
            {!disabled && (
                <div className="text-center mt-4">
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleUpload}
                        disabled={!selectedFile || hasFile} // Disable if no file is selected or a file already exists
                    >
                        Upload
                    </button>
                </div>
            )}
        </React.Fragment>
    );
};

export default SingleFileUpload;
